import { createAsyncThunk } from "@reduxjs/toolkit";
import { EXCHANGE_RATE } from "constants/api";
import axios from "utils/http";

export interface IExchangeRatesResponse extends ApiResponse {
  data: any;
}
export interface IExchangeRatesSettingsResponse extends ReduxAction {
  data: IExchangeRatesSettings;
}
export interface IUpdateExchangeRatesArgs extends ReduxAction {
  payload: IUpdateExchangeRates;
  id: number;
}
export interface IUpdatePayoutTypeArgs extends ReduxAction {
  payload: any;
  id: number | any;
}
export interface IDisableCurrencyPairArgs extends ReduxAction {
  payload: any;
  id: number | any;
}
export interface IUpdateExchangeFeeArgs extends ReduxAction {
  payload: { [x: string]: string };
}
export interface IUpdateChannelFeesArgs extends ReduxAction {
  payload: any;
  id: number | any;
}

export const fetchExchangeRates = createAsyncThunk(
  EXCHANGE_RATE.LIST,
  async (
    { page, queries, onFailure }: ReduxAction,
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams();

      if (Object.values(queries)?.length) {
        Object.entries(queries).forEach(([key, value]:any) => {
          if (value) {
            searchParams.append(`${key}`, value);
          }
        });
      }

      const { data }: IExchangeRatesResponse = await axios.get(
        `${EXCHANGE_RATE.LIST}?page=${page}`,
        {
          params: searchParams,
        }
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchSingleExchangeRate = createAsyncThunk(
  EXCHANGE_RATE.DETAIL,
  async ({ id, onFailure }: any, { rejectWithValue }) => {
    try {
      const { data }: any = await axios.get(
        EXCHANGE_RATE.DETAIL.replace(":id", String(id))
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchExchangeRateWithFee = createAsyncThunk(
  EXCHANGE_RATE.WITH_FEES,
  async ({ id, onFailure }: any, { rejectWithValue }) => {
    try {
      const { data }: any = await axios.get(
        EXCHANGE_RATE.WITH_FEES.replace(":id", String(id))
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchExchangeRatesSettings = createAsyncThunk(
  EXCHANGE_RATE.SETTINGS,
  async ({ onFailure }: ReduxAction, { rejectWithValue }) => {
    try {
      const { data }: IExchangeRatesSettingsResponse = await axios.get(
        EXCHANGE_RATE.SETTINGS
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateExchangeRates = createAsyncThunk(
  `${EXCHANGE_RATE.DETAIL}/update`,
  async (
    { payload, id, onSuccess, onFailure }: IUpdateExchangeRatesArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.put(
        EXCHANGE_RATE.DETAIL.replace(":id", String(id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updatePayoutType = createAsyncThunk(
  EXCHANGE_RATE.PAYOUT_TYPE,
  async (
    { payload, id, onSuccess, onFailure }: IUpdatePayoutTypeArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.patch(
        EXCHANGE_RATE.PAYOUT_TYPE.replace(":id", String(id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const disableCurrencyPair = createAsyncThunk(
  EXCHANGE_RATE.TOGGLE_PAIR_STATUS,
  async (
    { payload, id, onSuccess, onFailure }: IDisableCurrencyPairArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        EXCHANGE_RATE.TOGGLE_PAIR_STATUS.replace(":id", String(id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateExchangeFee = createAsyncThunk(
  EXCHANGE_RATE.EDIT_FEE,
  async (
    { payload, onSuccess, onFailure }: IUpdateExchangeFeeArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        EXCHANGE_RATE.EDIT_FEE,
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchChannelFees = createAsyncThunk(
  EXCHANGE_RATE.CHANNEL_FEES,
  async ({ onFailure }: ReduxAction, { rejectWithValue }) => {
    try {
      const { data }: any = await axios.get(
        EXCHANGE_RATE.CHANNEL_FEES
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchSingleChannelFee = createAsyncThunk(
  EXCHANGE_RATE.CHANNEL_FEE_DETAIL,
  async ({ id, onFailure }: any, { rejectWithValue }) => {
    try {
      const { data }: any = await axios.get(
        EXCHANGE_RATE.CHANNEL_FEE_DETAIL.replace(":id", String(id))
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateChannelFee = createAsyncThunk(
  EXCHANGE_RATE.UPDATE_CHANNEL_FEE,
  async (
    { payload, id, onSuccess, onFailure }: IUpdateChannelFeesArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.put(
        EXCHANGE_RATE.UPDATE_CHANNEL_FEE.replace(":id", String(id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);